import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  Company,
  Profile,
  StorageData,
  SubModules,
  User,
} from '@job-v2/ats-utils';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { AppGlobalService } from '../commons/services/app-global.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  Company as SociumCompany,
  StatesService,
  StateStoreKeys,
  User as SociumUser,
  UserRole,
  UserRolesPermissionsService,
} from '@socium/sociumflow';
import {
  Scopes,
  Applications,
  JOB_READ_ALL_JOBS,
  CanPermission,
} from '@socium/common-api';
import { hasAllScopes } from '@job-v2/shared';
import { LS_CURRENT_AUTH_USER } from '../commons/contants';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private readonly statesService = inject(StatesService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private httpClient = inject(HttpClient);
  subscription = new Subscription();
  private spinner = inject(NgxSpinnerService);
  private readonly userRolesPermissionsService = inject(
    UserRolesPermissionsService,
  );

  private readonly _isSociumAdmin = new BehaviorSubject<boolean>(false);
  public readonly isSociumAdmin$: Observable<boolean> =
    this._isSociumAdmin.asObservable();
  public isAdmin = false;
  public isSociumAdmin = false;
  public isManager = false;
  public isRecruiter = false;

  private selectedCompanyId = '';
  selectedCompanyIdSubject = new Subject<string>();

  private getUserData(): User | null {
    const user = this.statesService.getCurrentUser() as unknown as User | null;
    const authData = this.statesService.getCookie<StorageData>(
      StateStoreKeys.TOKEN_NAME,
    );

    if (user && authData?._accessToken) {
      const selectedCompany = this.statesService.getSelectedCompany() as unknown as Company | null;
      const userCompanyId = user.company?._id || user.company?.id;
      const selectedCompanyId = selectedCompany?._id || selectedCompany?.id;
      if (selectedCompany && userCompanyId && selectedCompanyId && userCompanyId !== selectedCompanyId) {
        const canSwitch =
          this.userRolesPermissionsService.hasSociumManagementAccess() ||
          user.role === UserRole.HOLDING_ADMIN ||
          user.role === UserRole.HR_SUPER_ADMIN;

        if (canSwitch) {
          const updatedUser = { ...user, company: selectedCompany };
          return updatedUser;
        }
      }
      return user;
    }

    return null;
  }

  defineUserRole() {
    const user = this.statesService.getCurrentUser() as unknown as User | null;
    if (user) {
      const adminScopes = [
        Scopes.JOB_DASHBOARDS,
        Scopes.JOB_OFFERS,
        Scopes.JOB_TEMPLATES,
        Scopes.JOB_CONFIGURATIONS,
      ];
      const managerScopes = [Scopes.JOB_TEMPLATES, Scopes.JOB_OFFERS];
      if (this.userRolesPermissionsService.hasSociumManagementAccess()) {
        this._isSociumAdmin.next(true);
        this.isAdmin = true;
      } else if (
        (this.userRolesPermissionsService.hasUserRolesPermissions(
          JOB_READ_ALL_JOBS,
        ) &&
          this.verifyUserScopes(user, adminScopes)) ||
        this.userRolesPermissionsService.hasUserAnyRoles([
          UserRole.HOLDING_ADMIN,
          UserRole.HR_SUPER_ADMIN,
        ])
      ) {
        this.isAdmin = true;
      } else if (
        this.userRolesPermissionsService.hasUserRolesPermissions(
          JOB_READ_ALL_JOBS,
        ) &&
        this.verifyUserScopes(user, managerScopes)
      ) {
        this.isManager = true;
      } else if (
        !this.userRolesPermissionsService.hasUserRolesPermissions(
          JOB_READ_ALL_JOBS,
        ) &&
        this.verifyUserScopes(user, [Scopes.JOB_OFFERS])
      ) {
        this.isRecruiter = true;
        return;
      }
    } else {
      this.isRecruiter = true;
    }
  }

  verifyUserScopes(user: User, scopes: Scopes[]) {
    const userAbilities =
      this.userRolesPermissionsService.getUserAbilities()[
        user.company?._id as string
      ] ?? [];
    let jobAbilities = userAbilities.filter((i) => i.application && i.application === Applications.JOB)
    const cleanedJobAbilities = jobAbilities.map((jb) => {
      if ((Array.isArray(jb.scope) && jb.scope.length === 1 && jb.scope.includes(Scopes.MODULE)) || jb.scope === Scopes.MODULE)
        return null
      return jb
    }).filter(Boolean) as CanPermission[]
    return hasAllScopes(scopes, cleanedJobAbilities);
  }

  async getAuthenticatedUser(redirectTo = true) {
    this.spinner.show();

    try {
      const localUser = this.getUserData();
      const queries = await firstValueFrom(this.activatedRoute.queryParams);
      const uuid = queries?.['_nav_t'];

      if (localUser && !uuid) {
        this.appGlobalService.currentUser.next(localUser);
        this.defineUserRole();
        if (this.router.url === '/home') this.handleRedirectionBasedOnRole();
        return;
      }

      if (uuid) {
        try {
          const data = await firstValueFrom(this.getTokenByTemporaryUUID(uuid));
          if (data) {
            const { token, currentUser, company } = JSON.parse(data);
            this.saveAuthData(token, currentUser, company);
            this.defineUserRole();
          } else {
            this.logout();
          }
        } catch (error) {
          this.logout();
        } finally {
          if (redirectTo) this.handleRedirectionBasedOnRole();
        }
      } else {
        this.logout();
      }
    } finally {
      this.spinner.hide();
    }
  }

  handleRedirectionBasedOnRole() {
    if (this.isAdmin)
      return this.router.navigate(['/dashboard'], {
        queryParams: { _nav_t: null },
        queryParamsHandling: 'merge',
      });
    else return this.router.navigate(['/management/jobs']);
  }

  async logout() {
    // `next(null)` suffit à vider l'utilisateur. NE PAS `complete()` ce
    // BehaviorSubject de service : un subject complété n'émet plus jamais,
    // ce qui casse définitivement `currentUser$` pour tous ses consommateurs
    // (listes de modèles, board, editor…) en cas de re-login sans rechargement.
    this.appGlobalService.currentUser.next(null);
    this.statesService.clearCurrentUser();
    this.statesService.clearSelectedCompany();
    this.statesService.clearCookie(StateStoreKeys.TOKEN_NAME);
    this.goBackToWorkSpace();
    return false;
  }

  getAccessToken() {
    const authData = this.statesService.getCookie<StorageData>(
      StateStoreKeys.TOKEN_NAME,
    );
    return authData?._accessToken ?? '';
  }

  getTokenByTemporaryUUID(uuid: string) {
    return this.httpClient.get(
      `${environment.workSpaceEndpoint}/auth/check-uuid?uuid=${uuid}`,
      { responseType: 'text' },
    );
  }

  getCurrentUserRoles(): string {
    const user = this.getUserData();
    return user?.role ?? UserRole.EMPLOYEE;
  }

  saveAuthData(token: string, currentUser: User, company: Company) {
    const expiry = new Date(Date.now() + 8 * 60 * 60 * 1000); // 8h par défaut
    const authData = {
      _accessToken: token,
      _tokenType: 'Bearer',
      _tokenExpirationDate: expiry,
    };

    this.statesService.setCookie(StateStoreKeys.TOKEN_NAME, authData, expiry);

    currentUser.company = company;
    this.statesService.setCurrentUser(currentUser as SociumUser);
    this.statesService.setSelectedCompany(company as unknown as SociumCompany);
    this.appGlobalService.currentUser.next(currentUser);
  }

  goBackToWorkSpace() {
    window.location.href = environment.workspaceUrl;
  }

  updateCompanyActive(company: Company) {
    this.spinner.show();
    const currentUser = this.appGlobalService.currentUser.value;
    if (currentUser?.company) {
      const canSwitch =
        this.userRolesPermissionsService.hasSociumManagementAccess() ||
        currentUser.role === UserRole.HOLDING_ADMIN ||
        currentUser.role === UserRole.HR_SUPER_ADMIN;

      if (canSwitch) {
        const updatedUser = { ...currentUser, company };
        this.appGlobalService.currentUser.next(updatedUser);
        this.statesService.setCurrentUser(updatedUser as unknown as SociumUser);
        this.statesService.setSelectedCompany(
          company as unknown as SociumCompany,
        );
      }
      this.spinner.hide();
    } else {
      this.spinner.hide();
    }
  }

  getAuthenticatedUserData(): SociumUser | null {
    const user =
      this.statesService.getCurrentUser()
    if (user) return user;
    return null;
  }

  getSelectedCompanyApplications(): Applications[] {
    const company =
      this.statesService.getSelectedCompany() as unknown as Company | null;
    return (company?.applications || []) as unknown as Applications[];
  }

  setSelectedCompanyId(companyId: string) {
    this.selectedCompanyId = companyId;
    this.emitSelectedCompanyId();
  }

  emitSelectedCompanyId() {
    this.selectedCompanyIdSubject.next(this.selectedCompanyId);
  }

  hasSubModule(subModule: SubModules): boolean {
    const user = this.statesService.getCurrentUser() as User;
    return !!user?.company?.subModules?.some((sm: any) => {
      if (typeof sm === 'string') return sm === subModule;
      return sm.name === subModule && !sm.deletedAt;
    });
  }

  getCompanyFromLocalStorage(): Company {
    return this.statesService.getSelectedCompany() as unknown as Company;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
